import React, { Fragment, useEffect, useState } from 'react';
import Navbar from '../../components/Navbar/Navbar';
import PageTitle from '../../components/pagetitle/PageTitle'
import { Button, Spinner } from 'react-bootstrap'
import { Input } from 'reactstrap';
import { useChangePasswordMutation, useCheckVerificationDigitCodeMutation } from '../../api/accountApi';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import './style.scss';

function VerifyDigitCode() {

    const [digitCode, setDigitCode] = useState('');
    const slug = useParams();
    const [sendVerifyDigit] = useCheckVerificationDigitCodeMutation();
    const [changePassword] = useChangePasswordMutation();
    const [isTrueDigitCode, setIsTrueDigitCode] = useState(false);
    const [loading, setLoading] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [errors, setErrors] = useState({});
    const [newPassword, setNewPassword] = useState({
        newPassword: '',
        confirmNewPassword: ''
    })
    const push = useNavigate();

    const handleSendDigitCodeValidation = async () => {
        if (!digitCode || digitCode.length !== 6) {
            setErrors({ digitCode: 'Please enter a valid 6-digit code' });
            toast.error('Please enter a valid 6-digit code');
            return;
        }

        setLoading(true);
        setErrors({});
        
        const verifyDigit = {
            email: slug.slug,
            digitCode: digitCode
        }
        
        try {
            const response = await sendVerifyDigit(verifyDigit);
            
            // Backend returns a boolean value directly
            if (response.data === true) {
                setIsTrueDigitCode(true);
                toast.success('Verification code confirmed successfully!');
            } else {
                setErrors({ digitCode: 'Invalid verification code' });
                toast.error('Invalid or expired verification code. Please try again.');
            }
        } catch (error) {
            console.error('Verification error:', error);
            setErrors({ digitCode: 'Verification failed' });
            toast.error('Verification failed. Please try again.');
        } finally {
            setLoading(false);
        }
    }

    const handleChangePassword = async () => {
        const validationErrors = {};

        if (!newPassword.newPassword || newPassword.newPassword.length < 6) {
            validationErrors.newPassword = 'Password must be at least 6 characters';
        }

        if (newPassword.newPassword !== newPassword.confirmNewPassword) {
            validationErrors.confirmNewPassword = 'Passwords do not match';
        }

        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            toast.error('Please fix the errors before submitting');
            return;
        }

        setLoading(true);
        setErrors({});

        const changePasswordModel = {
            email: slug.slug,
            password: newPassword.newPassword,
            confirmPassword: newPassword.confirmNewPassword
        }

        try {
            const response = await changePassword(changePasswordModel);
            console.log('Change password response:', response);
            
            if (response.data && response.data.isSuccess) {
                // Backend uses 'Messages' with capital M
                const successMessage = response.data.messages?.[0] || response.data.Messages?.[0] || 'Password changed successfully!';
                toast.success(successMessage);
                setTimeout(() => push("/login"), 1500);
            } else {
                const errorMessage = response.data?.errorMessages?.[0] || response.data?.ErrorMessages?.[0] || 'Failed to change password';
                toast.error(errorMessage);
                setLoading(false);
            }
        } catch (error) {
            console.error('Change password error:', error);
            toast.error('An error occurred. Please try again.');
            setLoading(false);
        }
    }


  return (
    <Fragment>
    <Navbar />
    <PageTitle pageTitle={isTrueDigitCode ? "Reset Password" : "Verify Code"} pagesub={"Account Security"} />
    <div className='verifyDigitWrapper'>
        {isTrueDigitCode === false ? (
            <div className='verifyDigitContainer'>
                <div className='verifyDigitCard'>
                    <div className='cardHeader'>
                        <h2>Verify Your Code</h2>
                        <p>We've sent a 6-digit verification code to <strong>{slug.slug}</strong></p>
                    </div>
                    
                    <div className='cardBody'>
                        <div className='inputGroup'>
                            <label>Verification Code</label>
                            <Input 
                                placeholder='Enter 6-digit code' 
                                className={`verifyInput ${errors.digitCode ? 'error' : ''}`}
                                type='text'
                                maxLength={6}
                                value={digitCode}
                                onChange={(e) => {
                                    const value = e.target.value.replace(/\D/g, '');
                                    setDigitCode(value);
                                    if (errors.digitCode) setErrors({});
                                }}
                                onKeyPress={(e) => {
                                    if (e.key === 'Enter') handleSendDigitCodeValidation();
                                }}
                            />
                            {errors.digitCode && (
                                <span className='errorMessage'>{errors.digitCode}</span>
                            )}
                        </div>
                        
                        <Button 
                            className='submitBtn' 
                            onClick={handleSendDigitCodeValidation}
                            disabled={loading || digitCode.length !== 6}
                        >
                            {loading ? <><Spinner animation="border" size="sm" /> Verifying...</> : 'Verify Code'}
                        </Button>

                        <div className='helpText'>
                            <p>Didn't receive the code? <a href='/forgot-password'>Resend Code</a></p>
                        </div>
                    </div>
                </div>
            </div>
        ) : (
            <div className='verifyDigitContainer'>
                <div className='verifyDigitCard'>
                    <div className='cardHeader'>
                        <h2>Reset Your Password</h2>
                        <p>Create a strong password for your account</p>
                    </div>
                    
                    <div className='cardBody'>
                        <div className='inputGroup'>
                            <label>New Password</label>
                            <div style={{ position: 'relative' }}>
                                <Input 
                                    placeholder='Enter new password' 
                                    className={`verifyInput ${errors.newPassword ? 'error' : ''}`}
                                    type={showNewPassword ? 'text' : 'password'}
                                    value={newPassword.newPassword}
                                    onChange={(e) => {
                                        setNewPassword((prevState) => ({...prevState, newPassword: e.target.value}));
                                        if (errors.newPassword) setErrors(prev => ({...prev, newPassword: ''}));
                                    }}
                                />
                                <span
                                    onClick={() => setShowNewPassword(!showNewPassword)}
                                    className='passwordToggle'
                                >
                                    {showNewPassword ? 'HIDE' : 'SHOW'}
                                </span>
                            </div>
                            {errors.newPassword && (
                                <span className='errorMessage'>{errors.newPassword}</span>
                            )}
                        </div>

                        <div className='inputGroup'>
                            <label>Confirm New Password</label>
                            <div style={{ position: 'relative' }}>
                                <Input 
                                    placeholder='Confirm new password' 
                                    className={`verifyInput ${errors.confirmNewPassword ? 'error' : ''}`}
                                    type={showConfirmPassword ? 'text' : 'password'}
                                    value={newPassword.confirmNewPassword}
                                    onChange={(e) => {
                                        setNewPassword((prevState) => ({...prevState, confirmNewPassword: e.target.value}));
                                        if (errors.confirmNewPassword) setErrors(prev => ({...prev, confirmNewPassword: ''}));
                                    }}
                                    onKeyPress={(e) => {
                                        if (e.key === 'Enter') handleChangePassword();
                                    }}
                                />
                                <span
                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                    className='passwordToggle'
                                >
                                    {showConfirmPassword ? 'HIDE' : 'SHOW'}
                                </span>
                            </div>
                            {errors.confirmNewPassword && (
                                <span className='errorMessage'>{errors.confirmNewPassword}</span>
                            )}
                        </div>
                        
                        <Button 
                            className='submitBtn' 
                            onClick={handleChangePassword}
                            disabled={loading}
                        >
                            {loading ? <><Spinner animation="border" size="sm" /> Updating...</> : 'Reset Password'}
                        </Button>
                    </div>
                </div>
            </div>
        )}
    </div>
</Fragment>
  )
}

export default VerifyDigitCode